<script lang="ts">
  import { Link, router } from "@inertiajs/svelte";
  import type { Trip, Expense, Schedule } from "../../lib/types";
  import { addNotification } from "../../lib/stores";
  import { formatCurrency } from "../../lib/utils/format";
  import Layout from "../../components/layout/Layout.svelte";
  import TripSubNav from "../../components/navigation/TripSubNav.svelte";
  import Modal from "../../components/overlay/Modal.svelte";

  let { trip, schedules = [], expenses = [], user = null }: {
    trip: Trip;
    schedules?: Schedule[];
    expenses?: Expense[];
    user?: { id: string; name: string; email: string; avatarUrl?: string } | null;
  } = $props();

  let generating = $state(false);
  let generatedContent = $state("");
  let editMode = $state(false);
  let showPreview = $state(false);
  let selectedTemplate = $state("blog");

  const templates = [
    { key: "blog", label: "블로그 스타일", icon: `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />`, description: "상세한 여행 후기" },
    { key: "sns", label: "SNS 스타일", icon: `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />`, description: "짧고 감각적인 포스팅" },
    { key: "report", label: "리포트 스타일", icon: `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />`, description: "비용 중심 정리" },
  ];

  // 날짜별 데이터 그룹화
  let tripDates = $derived(generateDateRange(trip.startDate, trip.endDate));
  let dayData = $derived(tripDates.map(date => ({
    date,
    dayNumber: tripDates.indexOf(date) + 1,
    schedules: schedules.filter(s => s.date === date).sort((a, b) => (a.startTime || "").localeCompare(b.startTime || "")),
    expenses: expenses.filter(e => e.transactedAt.split("T")[0] === date),
    totalSpent: expenses.filter(e => e.transactedAt.split("T")[0] === date).reduce((sum, e) => sum + e.krwAmountCents, 0),
  })));

  // 통계
  let stats = $derived({
    totalDays: tripDates.length,
    totalSpent: expenses.reduce((sum, e) => sum + e.krwAmountCents, 0),
    totalSchedules: schedules.length,
    topCategory: getTopCategory(),
    avgDailySpent: Math.round(expenses.reduce((sum, e) => sum + e.krwAmountCents, 0) / tripDates.length),
  });

  const categories = {
    food: { label: "식비", icon: "🍽️" },
    transport: { label: "교통", icon: "🚗" },
    accommodation: { label: "숙소", icon: "🏨" },
    shopping: { label: "쇼핑", icon: "🛍️" },
    activity: { label: "액티비티", icon: "🎯" },
    other: { label: "기타", icon: "📌" },
  };

  function generateDateRange(start: string, end: string): string[] {
    const dates: string[] = [];
    const startDate = new Date(start);
    const endDate = new Date(end);
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      dates.push(d.toISOString().split("T")[0]);
    }
    return dates;
  }

  function getTopCategory(): string {
    const counts: Record<string, number> = {};
    expenses.forEach(e => {
      counts[e.category] = (counts[e.category] || 0) + e.krwAmountCents;
    });
    const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    return sorted[0]?.[0] || "other";
  }

  function formatDate(dateStr: string): string {
    return new Date(dateStr).toLocaleDateString("ko-KR", {
      year: "numeric",
      month: "long",
      day: "numeric",
      weekday: "long",
    });
  }

  function getCountryFlag(countryCode: string): string {
    if (!countryCode) return "🌍";
    const codePoints = countryCode.toUpperCase().split("").map(char => 127397 + char.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
  }

  async function generateStory() {
    generating = true;
    
    // AI 없이 템플릿 기반으로 생성
    await new Promise(resolve => setTimeout(resolve, 1500)); // 시뮬레이션

    if (selectedTemplate === "blog") {
      generatedContent = generateBlogStyle();
    } else if (selectedTemplate === "sns") {
      generatedContent = generateSnsStyle();
    } else {
      generatedContent = generateReportStyle();
    }

    generating = false;
    editMode = true;
  }

  function generateBlogStyle(): string {
    let content = `# ${getCountryFlag(trip.countryCode || "")} ${trip.name}\n\n`;
    content += `> ${trip.destination}에서의 ${stats.totalDays}일간의 여행 기록\n\n`;
    content += `---\n\n`;

    dayData.forEach(day => {
      content += `## Day ${day.dayNumber} - ${formatDate(day.date)}\n\n`;
      
      if (day.schedules.length > 0) {
        day.schedules.forEach(s => {
          const cat = categories[s.category as keyof typeof categories] || categories.other;
          content += `### ${cat.icon} ${s.title}\n`;
          if (s.location) content += `📍 ${s.location}\n`;
          if (s.memo) content += `\n${s.memo}\n`;
          content += `\n`;
        });
      } else {
        content += `*일정 기록이 없습니다.*\n\n`;
      }

      if (day.expenses.length > 0) {
        content += `💰 **이 날의 지출: ₩${formatCurrency(day.totalSpent)}**\n\n`;
        day.expenses.forEach(e => {
          const cat = categories[e.category as keyof typeof categories] || categories.other;
          content += `- ${cat.icon} ${e.merchantName}: ₩${formatCurrency(e.krwAmountCents)}\n`;
        });
        content += `\n`;
      }

      content += `---\n\n`;
    });

    content += `## 📊 여행 총정리\n\n`;
    content += `- **총 여행 기간**: ${stats.totalDays}일\n`;
    content += `- **총 지출 금액**: ₩${formatCurrency(stats.totalSpent)}\n`;
    content += `- **일일 평균 지출**: ₩${formatCurrency(stats.avgDailySpent)}\n`;
    content += `- **가장 많이 쓴 카테고리**: ${categories[stats.topCategory as keyof typeof categories]?.label || "기타"}\n\n`;
    content += `---\n\n`;
    content += `*이 여행기는 TripHelper로 자동 생성되었습니다.*`;

    return content;
  }

  function generateSnsStyle(): string {
    let content = `${getCountryFlag(trip.countryCode || "")} ${trip.destination} ${stats.totalDays}일 여행 완료!\n\n`;
    
    const highlights = dayData
      .filter(d => d.schedules.length > 0)
      .slice(0, 3)
      .map(d => d.schedules[0]?.title)
      .filter(Boolean);
    
    if (highlights.length > 0) {
      content += `✨ 하이라이트\n`;
      highlights.forEach(h => content += `• ${h}\n`);
      content += `\n`;
    }

    content += `💸 총 지출: ₩${formatCurrency(stats.totalSpent)}\n`;
    content += `📅 ${stats.totalDays}일 / ${stats.totalSchedules}개 일정\n\n`;
    content += `#${trip.destination.replace(/\s/g, "")} #여행 #${stats.totalDays}일여행 #TripHelper`;

    return content;
  }

  function generateReportStyle(): string {
    let content = `# ${trip.name} 여행 정산 리포트\n\n`;
    content += `**기간**: ${trip.startDate} ~ ${trip.endDate} (${stats.totalDays}일)\n`;
    content += `**목적지**: ${trip.destination}\n`;
    content += `**참여자**: ${trip.participants?.length || 1}명\n\n`;

    content += `## 지출 요약\n\n`;
    content += `| 항목 | 금액 |\n`;
    content += `|------|------|\n`;
    content += `| 총 지출 | ₩${formatCurrency(stats.totalSpent)} |\n`;
    content += `| 1인당 | ₩${formatCurrency(Math.round(stats.totalSpent / (trip.participants?.length || 1)))} |\n`;
    content += `| 일 평균 | ₩${formatCurrency(stats.avgDailySpent)} |\n\n`;

    content += `## 카테고리별 지출\n\n`;
    content += `| 카테고리 | 금액 | 비율 |\n`;
    content += `|----------|------|------|\n`;
    
    const catTotals: Record<string, number> = {};
    expenses.forEach(e => {
      catTotals[e.category] = (catTotals[e.category] || 0) + e.krwAmountCents;
    });
    
    Object.entries(catTotals)
      .sort((a, b) => b[1] - a[1])
      .forEach(([cat, amount]) => {
        const catInfo = categories[cat as keyof typeof categories] || categories.other;
        const ratio = stats.totalSpent > 0 ? ((amount / stats.totalSpent) * 100).toFixed(1) : "0";
        content += `| ${catInfo.icon} ${catInfo.label} | ₩${formatCurrency(amount)} | ${ratio}% |\n`;
      });

    content += `\n## 일별 지출\n\n`;
    content += `| 날짜 | 지출 |\n`;
    content += `|------|------|\n`;
    dayData.forEach(day => {
      content += `| Day ${day.dayNumber} (${day.date}) | ₩${formatCurrency(day.totalSpent)} |\n`;
    });

    return content;
  }

  function copyToClipboard() {
    navigator.clipboard.writeText(generatedContent);
    addNotification("success", "클립보드에 복사되었습니다!");
  }

  function downloadMarkdown() {
    const blob = new Blob([generatedContent], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${trip.name}_여행기.md`;
    a.click();
    URL.revokeObjectURL(url);
    addNotification("success", "파일이 다운로드되었습니다!");
  }
</script>

<Layout {user}>
  <div class="min-h-screen bg-app">
    <!-- Trip Sub Navigation -->
    <TripSubNav {trip} currentPage="story" />

    <!-- Story Header -->
    <header class="bg-surface border-b border">
      <div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <!-- Trip Info -->
        <div class="flex items-center gap-4 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl">
          <span class="text-4xl">{getCountryFlag(trip.countryCode || "")}</span>
          <div>
            <h2 class="font-semibold text-primary flex items-center gap-1.5"><svg class="w-4 h-4 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" /></svg> Story Studio</h2>
            <p class="text-sm text-secondary">{trip.destination} • {stats.totalDays}일 여행기</p>
          </div>
          <div class="ml-auto flex gap-6 text-center">
            <div>
              <p class="text-2xl font-bold text-purple-600">{stats.totalSchedules}</p>
              <p class="text-xs text-muted">일정</p>
            </div>
            <div>
              <p class="text-2xl font-bold text-pink-600">₩{formatCurrency(stats.totalSpent)}</p>
              <p class="text-xs text-muted">총 지출</p>
            </div>
          </div>
        </div>
      </div>
    </header>

    <main class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {#if !editMode}
        <!-- Template Selection -->
        <div class="bg-surface rounded-2xl shadow-sm p-6 mb-6">
          <h3 class="text-lg font-semibold text-primary mb-4">템플릿 선택</h3>
          <div class="grid grid-cols-3 gap-4">
            {#each templates as template}
              <button
                onclick={() => selectedTemplate = template.key}
                class="p-4 rounded-xl border-2 text-left transition-all
                  {selectedTemplate === template.key 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-bright hover:border-bright'}"
              >
                <div class="w-10 h-10 rounded-xl flex items-center justify-center mb-2" style="background: rgba(59,130,246,0.12);">
                  <svg class="w-6 h-6" style="color: #3b82f6;" fill="none" stroke="currentColor" viewBox="0 0 24 24">{@html template.icon}</svg>
                </div>
                <h4 class="font-medium text-primary mt-2">{template.label}</h4>
                <p class="text-sm text-muted mt-1">{template.description}</p>
              </button>
            {/each}
          </div>
        </div>

        <!-- Data Preview -->
        <div class="bg-surface rounded-2xl shadow-sm p-6 mb-6">
          <h3 class="text-lg font-semibold text-primary mb-4">사용할 데이터</h3>
          <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div class="p-4 bg-surface-hover rounded-xl text-center">
              <p class="text-3xl font-bold text-primary">{stats.totalDays}</p>
              <p class="text-sm text-muted">여행 일수</p>
            </div>
            <div class="p-4 bg-surface-hover rounded-xl text-center">
              <p class="text-3xl font-bold text-primary">{stats.totalSchedules}</p>
              <p class="text-sm text-muted">일정 수</p>
            </div>
            <div class="p-4 bg-surface-hover rounded-xl text-center">
              <p class="text-3xl font-bold text-primary">{expenses.length}</p>
              <p class="text-sm text-muted">지출 내역</p>
            </div>
            <div class="p-4 bg-surface-hover rounded-xl text-center">
              <p class="text-xl font-bold text-primary">₩{formatCurrency(stats.totalSpent)}</p>
              <p class="text-sm text-muted">총 지출</p>
            </div>
          </div>
        </div>

        <!-- Generate Button -->
        <div class="text-center">
          <button
            onclick={generateStory}
            disabled={generating}
            class="px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-xl font-medium text-lg shadow-lg hover:shadow-xl transition-all disabled:opacity-50"
          >
            {#if generating}
              <span class="flex items-center gap-2">
                <svg class="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                여행기 생성 중...
              </span>
            {:else}
              <svg class="w-5 h-5 inline-block mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" /></svg> 여행기 자동 생성
            {/if}
          </button>
        </div>
      {:else}
        <!-- Editor -->
        <div class="bg-surface rounded-2xl shadow-sm overflow-hidden">
          <!-- Toolbar -->
          <div class="flex items-center justify-between px-6 py-4 border-b border">
            <div class="flex items-center gap-2">
              <button
                onclick={() => editMode = false}
                class="px-4 py-2 text-secondary hover:bg-surface-hover rounded-lg"
              >
                ← 다시 생성
              </button>
            </div>
            <div class="flex items-center gap-2">
              <button
                onclick={() => showPreview = !showPreview}
                class="px-4 py-2 bg-surface-hover text-secondary rounded-lg hover:bg-surface-hover"
              >
                {showPreview ? "편집" : "미리보기"}
              </button>
              <button
                onclick={copyToClipboard}
                class="px-4 py-2 bg-surface-hover text-secondary rounded-lg hover:bg-surface-hover"
              >
                <svg class="w-4 h-4 inline-block mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg> 복사
              </button>
              <button
                onclick={downloadMarkdown}
                class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <svg class="w-4 h-4 inline-block mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg> 다운로드
              </button>
            </div>
          </div>

          <!-- Content -->
          <div class="p-6">
            {#if showPreview}
              <div class="prose prose-lg max-w-none">
                {@html generatedContent
                  .replace(/^# (.*$)/gm, '<h1 class="text-3xl font-bold mt-8 mb-4">$1</h1>')
                  .replace(/^## (.*$)/gm, '<h2 class="text-2xl font-semibold mt-6 mb-3 text-primary">$1</h2>')
                  .replace(/^### (.*$)/gm, '<h3 class="text-xl font-medium mt-4 mb-2">$1</h3>')
                  .replace(/^\> (.*$)/gm, '<blockquote class="border-l-4 border-blue-500 pl-4 italic text-secondary">$1</blockquote>')
                  .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                  .replace(/^\- (.*$)/gm, '<li class="ml-4">$1</li>')
                  .replace(/^---$/gm, '<hr class="my-6 border-bright">')
                  .replace(/\n/g, '<br>')}
              </div>
            {:else}
              <textarea
                bind:value={generatedContent}
                class="w-full h-[600px] p-4 font-mono text-sm border border-bright rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                placeholder="여행기 내용..."
              ></textarea>
            {/if}
          </div>
        </div>
      {/if}
    </main>
  </div>
</Layout>
